import { bytes, since } from "../board.js";
import { Brand, Calm, Certificates, Clock, DeployCard, Feed, Gauge, IncidentCard, Label, Matrix, Orb, headlineOf, toneOf } from "../parts.jsx";

/**
 * Layout 3 — Stream.
 *
 * Time on the left, state on the right. Everything that happens — a deployment
 * starting and finishing, an incident opening and closing, a certificate
 * running out — flows down the left half newest first, with anything still in
 * progress pinned above the flow. The right half is the box as a matrix: one
 * square per watched component, so a red one is a red square in a field of
 * green, and the resources beneath.
 */
export function Stream({ board, projects, feed, screenKey, now }) {
    const headline = headlineOf(board);
    const h = toneOf(headline.tone);
    const { counts } = board.status;
    const active = board.deployments.active;
    const open = board.incidents.open;
    const { box } = board;
    const flowing = feed.filter((event) => !(event.kind === "deploy" && event.live));

    return (
        <div className="grid h-full grid-rows-[auto_minmax(0,1fr)] gap-[1.2rem]">
            <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                <Brand sub="stream" />
                <div className="flex items-center gap-4">
                    <Orb tone={headline.tone} size="2.4rem" />
                    <div>
                        <div className={`text-[1.6rem] font-semibold leading-none tracking-[0.08em] ${h.text}`}>{headline.text}</div>
                        <div className="live-label mt-1.5">{counts.up}/{counts.total} answering · {open.length} open · checked {since(board.status.checkedAt, now)} ago</div>
                    </div>
                </div>
                <Clock />
            </header>

            <section className="grid min-h-0 grid-cols-1 gap-[1.2rem] lg:grid-cols-[1fr_1.15fr]">
                <div className="flex min-h-0 flex-col gap-[0.8rem] overflow-hidden">
                    {active.map((deployment) => (
                        <DeployCard key={deployment.id} deployment={deployment} project={projects.get(deployment.slug)} screenKey={screenKey} compact={deployment.status !== "running"} siblings={active} />
                    ))}
                    <div className="flex items-baseline justify-between">
                        <Label>timeline</Label>
                        <span className="live-num text-[0.8rem] text-gray-600">{flowing.length} events</span>
                    </div>
                    <div className="min-h-0 flex-1 overflow-hidden [mask-image:linear-gradient(to_bottom,black_85%,transparent)]">
                        <Feed events={flowing} limit={16} projects={projects} screenKey={screenKey} />
                    </div>
                </div>

                <div className="grid min-h-0 grid-rows-[minmax(0,1fr)_auto] gap-[1.2rem]">
                    <div className="live-card flex min-h-0 flex-col gap-[1rem] p-[1.2rem]">
                        <div className="flex items-baseline justify-between">
                            <Label>the box</Label>
                            <span className="live-num text-[0.8rem] text-gray-600">{counts.total} watched · {box.processes.online}/{box.processes.total} processes</span>
                        </div>
                        <div className="min-h-0 flex-1 overflow-hidden">
                            <Matrix projects={[...projects.values()]} screenKey={screenKey} />
                        </div>
                        {open.length ? (
                            <div className="flex flex-col gap-[0.6rem]">
                                {open.slice(0, 3).map((incident) => <IncidentCard key={incident.id} incident={incident} />)}
                            </div>
                        ) : (
                            <Calm title="No open incidents" className="py-2" />
                        )}
                        <Certificates certificates={board.certificates} />
                    </div>
                    <div className="grid grid-cols-1 gap-[1rem] md:grid-cols-3">
                        <Gauge label="cpu" ratio={(box.cpu ?? 0) / 100} text={box.cpu === null ? "—" : `${Math.round(box.cpu)}%`} sub={`load ${box.load?.[0]?.toFixed(2) ?? "—"}`} trace={{ values: box.history.cpu, max: 100 }} />
                        <Gauge label="memory" ratio={box.memory.total ? box.memory.used / box.memory.total : 0} text={box.memory.total ? `${Math.round((box.memory.used / box.memory.total) * 100)}%` : "—"} sub={`${bytes(box.memory.used)} / ${bytes(box.memory.total)}`} trace={{ values: box.history.memory, max: box.memory.total }} />
                        <Gauge label="disk" ratio={box.disk.total ? box.disk.used / box.disk.total : 0} text={box.disk.total ? `${Math.round((box.disk.used / box.disk.total) * 100)}%` : "—"} sub={`${bytes(box.disk.total ? box.disk.total - box.disk.used : null)} free`} />
                    </div>
                </div>
            </section>
        </div>
    );
}
